import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import MetodoPagoField from "../shared/MetodoPagoField";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { Badge } from "../badge";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import {
  formatCalendarDate,
  calendarDayAnchor,
  parseDateInputToTimestamp,
  todayInputValue,
} from "../../utils/dates";
import { ACCENT, formatMXN } from "./shared";
import { costosPorPagar } from "../../utils/costos";

// Espejo de CuentasPorCobrar del lado del gasto: qué debe el venue, a quién,
// y de qué evento sale. A diferencia de Por cobrar, esta vista SÍ escribe:
// marcar pagado es la acción natural aquí.
const CuentasPorPagar = () => {
  const navigate = useNavigate();
  const { selectedVenueId, costos, costosLoading, events, calendarTimezone, metodosPagoConDefaults: metodosPago } = useVenue();
  const { userInfo, user } = useAuth();
  // Inversionista (P-FRA-13): ve lo que se debe, no lo marca pagado.
  const soloLectura = esSoloLectura(userInfo);
  const [agrupacion, setAgrupacion] = useState("proveedor"); // proveedor | evento
  // Fila que está confirmando su fecha de pago: { costoId, fecha: "YYYY-MM-DD" }
  const [pagando, setPagando] = useState(null);

  const eventsById = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => { map[e.id] = e; });
    return map;
  }, [events]);

  const pendientes = useMemo(() => {
    const lista = costosPorPagar(costos);
    return lista.sort(
      (a, b) =>
        (calendarDayAnchor(a.fecha, calendarTimezone) || 0) -
        (calendarDayAnchor(b.fecha, calendarTimezone) || 0)
    );
  }, [costos, calendarTimezone]);

  const total = pendientes.reduce((s, c) => s + (Number(c.monto) || 0), 0);

  const grupos = useMemo(() => {
    const map = {};
    pendientes.forEach((c) => {
      let key, label, sub;
      if (agrupacion === "proveedor") {
        key = c.proveedorId || `cat:${c.categoria || "Otro"}`;
        label = c.proveedorNombre || c.categoria || "Otro";
        sub = c.proveedorId ? "Proveedor" : "Sin proveedor";
      } else {
        key = c.eventId || "general";
        const ev = c.eventId ? eventsById[c.eventId] : null;
        label = c.eventId
          ? ev?.nombre || "Evento no encontrado"
          : "Gastos generales del venue";
        sub = c.eventId ? "Evento" : "Sin evento";
      }
      const g = map[key] || (map[key] = { key, label, sub, total: 0, items: [] });
      g.total += Number(c.monto) || 0;
      g.items.push(c);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [pendientes, agrupacion, eventsById]);

  // Marcar pagado pide la fecha en que salió el dinero, con hoy como
  // propuesta (FRA-342): el costo entra al ledger con su fecha de pago, no
  // con la fecha en que se causó.
  const marcarPagado = async (costo, fechaInput, metodo = "") => {
    const fechaPago = parseDateInputToTimestamp(fechaInput, calendarTimezone);
    if (!fechaPago) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "venues", selectedVenueId, "costos", costo.id), {
        estado: "pagado",
        fechaPago,
        // Cómo se pagó: mismo catálogo que pagos y costos (utils/metodosPago.js).
        ...(metodo ? { metodo } : {}),
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO,
          entity: "costo",
          entityId: costo.id,
          entityName: costo.concepto || "",
          details: {
            estado: "pagado",
            monto: Number(costo.monto) || 0,
            fechaPago: fechaInput,
            ...(metodo ? { metodo } : {}),
          },
        },
        userInfo,
        user
      );
      setPagando(null);
      showAlert("Costo marcado como pagado", "success");
    } catch (err) {
      showAlert(`No se pudo actualizar. [${err.message}]`, "error");
    }
  };

  if (costosLoading) {
    return <div className="text-center py-12 text-zinc-400">Cargando...</div>;
  }

  if (pendientes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-zinc-900 dark:text-white font-semibold">
          No debes nada registrado
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Aquí aparecen los costos en estado &ldquo;por pagar&rdquo;, de eventos
          y generales.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-2">
          <div className="text-xs uppercase tracking-wide font-semibold text-amber-700">
            Total comprometido
          </div>
          <div className="text-xl font-bold text-amber-700">{formatMXN(total)}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-500">Agrupar por</span>
          <select
            value={agrupacion}
            onChange={(e) => setAgrupacion(e.target.value)}
            className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm"
          >
            <option value="proveedor">Proveedor</option>
            <option value="evento">Evento</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {grupos.map((g) => (
          <div
            key={g.key}
            className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800/40">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-zinc-900 dark:text-white truncate">
                  {g.label}
                </div>
                <div className="text-xs text-zinc-500">{g.sub}</div>
              </div>
              <div className="text-sm font-bold text-amber-700 whitespace-nowrap">
                {formatMXN(g.total)}
              </div>
            </div>
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {g.items.map((c) => {
                  const ev = c.eventId ? eventsById[c.eventId] : null;
                  return (
                    <tr key={c.id}>
                      <td className="px-4 py-2 whitespace-nowrap text-zinc-500 w-32">
                        {formatCalendarDate(c.fecha, calendarTimezone, { style: "short" }) || "—"}
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-zinc-900 dark:text-white">{c.concepto || "—"}</div>
                        <div className="text-xs text-zinc-400">
                          {c.categoria || "Otro"}
                          {c.origen === "proveedor_auto" && " · automático"}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {ev ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
                            className="text-sm hover:underline"
                            style={{ color: ACCENT }}
                          >
                            {ev.nombre || "Sin nombre"}
                          </button>
                        ) : (
                          <span className="text-xs text-zinc-400">
                            {c.eventId ? "Evento no encontrado" : "Gasto general"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {formatMXN(c.monto)}
                      </td>
                      {!soloLectura && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {pagando?.costoId === c.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input
                              type="date"
                              value={pagando.fecha}
                              onChange={(e) =>
                                setPagando({ ...pagando, fecha: e.target.value })
                              }
                              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-xs"
                              title="Fecha en que salió el dinero"
                            />
                            <MetodoPagoField
                              value={pagando.metodo}
                              onChange={(v) => setPagando({ ...pagando, metodo: v })}
                              metodosPago={metodosPago}
                              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-xs"
                              vacio="Método"
                            />
                            <button
                              type="button"
                              onClick={() => marcarPagado(c, pagando.fecha, pagando.metodo)}
                              className="px-2 py-1 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                              style={{ backgroundColor: "#1b4d2e" }}
                            >
                              Confirmar
                            </button>
                            <button
                              type="button"
                              onClick={() => setPagando(null)}
                              className="text-xs text-zinc-400 hover:text-zinc-600 px-1"
                            >
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setPagando({
                                costoId: c.id,
                                fecha: todayInputValue(),
                                metodo: c.metodo || "",
                              })
                            }
                            title="Marcar como pagado"
                          >
                            <Badge color="amber">Marcar pagado</Badge>
                          </button>
                        )}
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CuentasPorPagar;
